/**
 * A grade, desenhada pelo motor — sem passar pelo modulo antigo.
 *
 * Este arquivo e o que fecha a Fase 6 para o `datagrid`. Antes, o unico
 * componente que sabia desenhar uma grade morava no `builder-screen-beta`, e o
 * motor so o alcancava por um renderer emprestado (o token `NODE_RENDERERS`).
 * Isso funcionava, mas amarrava toda tela com grade — inclusive as publicadas —
 * ao modulo que a Fase 8 apaga.
 *
 * O componente foi COPIADO para `screen-engine/components/data-grid`, nao
 * reescrito: e o limite de escopo mais importante do plano. O que mudou na copia
 * esta documentado la — standalone, OnPush e o estado do construtor por token.
 *
 * O antigo continua no lugar, intocado, servindo o canvas legado, o
 * `template-detail` e o launcher ate a Fase 8. Duas copias vivas ao mesmo tempo
 * seria problema se as duas fossem editadas; a antiga esta parada.
 */
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { SCREEN_RUNTIME } from '../../runtime/screen-runtime';

import { ScreenNode } from '../../model/node-props';
import { RenderMode } from '../../render/node-renderer';
import { LegacyGridProps, nodeToGridProps } from '../../render/bridge/node-to-grid-props';
import { translateTAValue } from '../../../system-utils/pipes/translateTA';
import { DataGridComponent } from './data-grid.component';
import { LEGACY_SCREEN_STATE } from '../legacy-screen-state';
import { LegacyStateShim } from '../legacy-state-shim';

@Component({
  selector: 'tem-datagrid-renderer',
  standalone: true,
  imports: [DataGridComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    // A grade pergunta "quais nos estao dentro deste painel?" para resolver
    // mestre-detalhe. O shim responde a partir do `ScreenDocument`, sem o
    // servico de 3.132 linhas do modulo antigo. Ver `legacy-screen-state.ts`.
    { provide: LEGACY_SCREEN_STATE, useClass: LegacyStateShim }
  ],
  template: `
    <tem-data-grid
      [props]="gridProps"
      [effectiveDataPath]="effectiveDataPath"
      [previewMode]="previewMode"
      (rowSelected)="onRowSelected()" />
  `
})
/**
 * NAO declara `implements NodeRenderer`.
 *
 * O contrato tipa as entradas como `InputSignal`, e este renderer usa `@Input()`
 * classico porque precisa do `ngOnChanges` para so recriar as props quando o NO
 * muda — recria-las a cada ciclo faria a grade recarregar dados sem parar. As
 * tres entradas sao as mesmas de qualquer renderer; o que muda e a forma, e o
 * `renderer-map.spec.ts` continua garantindo que todo tipo tem um renderer.
 */
export class DataGridRenderer implements OnChanges {

  private readonly translate = inject(TranslateService);

  /**
   * O runtime de comportamento, quando houver.
   *
   * Opcional porque a grade também aparece em contexto sem runtime — a prévia
   * de um editor, por exemplo. `null` ali é o caso normal, não uma falha.
   */
  private readonly runtime = inject(SCREEN_RUNTIME, { optional: true });

  /**
   * Uma linha foi escolhida: avisa o runtime.
   *
   * O `onRowSelect` é o único gatilho que a grade produz, e ele é o mais pedido
   * — "escolher a linha preenche o formulário ao lado" é o caso de uso inteiro
   * do mestre-detalhe.
   */
  onRowSelected(): void {
    this.runtime?.trigger(this.node.id, 'onRowSelect');
  }

  @Input() node!: ScreenNode<'datagrid'>;
  @Input() form: FormGroup | null = null;
  @Input() mode: RenderMode = 'view';

  gridProps: LegacyGridProps = {};
  effectiveDataPath: string | undefined = undefined;

  /**
   * Dados falsos SO na previa do editor, nunca em producao.
   *
   * Hoje as grades do editor mostram `fake-data-generator` sem dizer que e
   * falso, o que e a origem de "funcionou no construtor, quebrou em producao".
   * Amarrar ao `mode` e o primeiro passo: em `view` nao ha caminho pelo qual
   * dado inventado apareca.
   */
  get previewMode(): boolean {
    return this.mode === 'editPreview';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['node'] === undefined || this.node === undefined) {
      return;
    }

    // Objeto novo a cada mudanca do NO, e so nela: a grade reage por
    // `ngOnChanges`, entao recriar as props a cada ciclo de deteccao a faria
    // recarregar dados sem parar.
    //
    // O tradutor entra por aqui: `headerKey` pode ser chave de traducao, e a
    // grade so sabe exibir texto pronto. Ver `grid-columns.ts`.
    this.gridProps = nodeToGridProps(
      this.node,
      key => translateTAValue(this.translate, key)
    );
    this.effectiveDataPath = this.node.binding?.dataPath;
  }
}
